import React from 'react';
import { Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Select from 'react-select';
import dateFormat from 'dateformat';
import { useAsyncResource } from 'use-async-resource';
import FileSaver from 'file-saver';
import ExcelJS from 'exceljs';

import { MapBranch } from '../../models/dashboard-models';
import { PaginationComponent } from '../../components/Pagination';
import { usePagination } from '../../utils/pagination-hooks';
import { useGetConfigInvoiceDetails, useGetEssencePriceGroups } from '../../utils/configuration-hooks';
import { DistributorOption } from './DistributorTransferTab';
import {
    DeviceCountBranchRow,
    DeviceCountDevice,
    DevicePricesByDistributorRow,
    DevicePricesResponse,
    DeviceSummaryResponse,
    MaintenanceStatusFilter,
} from './types';
import { getDaysUntilDate } from './deviceUtils';
import { DatePickerWrapper } from '../../components/DatePickerWrapper';

const DEVICE_MODEL_COLORS = [
    '#fa3636',
    '#2774c0',
    '#e28738',
    '#7556a8',
    '#fa61e0',
    '#e04b96',
    '#3f8f9f',
    '#8b5e34',
    '#7d8f3f',
    '#c4513f',
    '#4d6f42',
    '#5f5aa2',
    '#c08a2f',
    '#a0446f',
];
const PRICE_COLORS = [
    '#6d7786',
    '#682f26',
    '#d87135',
    '#50a729',
    '#c7295d',
    '#9454ad',
    '#4d82b8',
    '#a19a2e',
    '#57df35',
    '#2a2b5e',
    '#b85e71',
    '#9a7651',
    '#6f8f55',
    '#4f9088',
];
const UNKNOWN_PRICE_GROUP_NUMBER = 0;

interface DevicesTabProps {
    t: (key: string) => string;
    pageSize: number;
    setPagination: (value: { pageSize: number; pageStart: number }) => void;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    exportLoading: boolean;
    fetchDevicesForExport: () => Promise<DeviceCountBranchRow[]>;
    distributors: { items: DistributorOption[] };
    selectedDistributor: number | null;
    setSelectedDistributor: React.Dispatch<React.SetStateAction<number | null>>;
    selectedDeviceModel: string | null;
    setSelectedDeviceModel: React.Dispatch<React.SetStateAction<string | null>>;
    selectedFromDate: string;
    setSelectedFromDate: React.Dispatch<React.SetStateAction<string>>;
    selectedToDate: string;
    setSelectedToDate: React.Dispatch<React.SetStateAction<string>>;
    selectedMaintenanceSort: MaintenanceStatusFilter | null;
    setSelectedMaintenanceSort: React.Dispatch<React.SetStateAction<MaintenanceStatusFilter | null>>;
    sortedDevices: DeviceCountBranchRow[] | null;
    devicesLoading: boolean;
    getMaintenanceRowStyle: (branch: DeviceCountBranchRow) => React.CSSProperties;
    FetchData: (branch: MapBranch | DeviceCountBranchRow) => Promise<void>;
    getBranchCompanyName: (branch: DeviceCountBranchRow) => string;
    groupDevicesById: (branchDevices: DeviceCountDevice[]) => Array<[number, DeviceCountDevice[]]>;
    distributorsById: Map<number, string>;
    totalElements: number;
    pagination: ReturnType<typeof usePagination>;
    summaryLoading: boolean;
    devicesSummary: DeviceSummaryResponse | null;
    pricesLoading: boolean;
    devicesPrices: DevicePricesResponse | null;
    summaryFilterText: string;
    pricesFilterText: string;
}

export const DevicesTab = ({
    t,
    pageSize,
    setPagination,
    setCurrentPage,
    exportLoading,
    fetchDevicesForExport,
    distributors,
    selectedDistributor,
    setSelectedDistributor,
    selectedDeviceModel,
    setSelectedDeviceModel,
    selectedFromDate,
    setSelectedFromDate,
    selectedToDate,
    setSelectedToDate,
    selectedMaintenanceSort,
    setSelectedMaintenanceSort,
    sortedDevices,
    devicesLoading,
    getMaintenanceRowStyle,
    FetchData,
    getBranchCompanyName,
    groupDevicesById,
    distributorsById,
    totalElements,
    pagination,
    summaryLoading,
    devicesSummary,
    pricesLoading,
    devicesPrices,
    summaryFilterText,
    pricesFilterText,
}: DevicesTabProps) => {
    const [readEssencePriceGroups] = useAsyncResource(useGetEssencePriceGroups, []);
    const [readInvoiceDetails] = useAsyncResource(useGetConfigInvoiceDetails, []);
    const essencePriceGroups = readEssencePriceGroups();
    const invoiceDetails = readInvoiceDetails();
    const currencyConfiguration = invoiceDetails?.currency ?? '€';
    const [selectedPriceGroupFilters, setSelectedPriceGroupFilters] = React.useState<number[]>([]);
    const [devicesExportLoading, setDevicesExportLoading] = React.useState(false);
    const [pricesExportLoading, setPricesExportLoading] = React.useState(false);
    const isMaintenanceFilterSelected = (filter: MaintenanceStatusFilter) => selectedMaintenanceSort === filter;
    const getMaintenanceFilterClassName = (filter: MaintenanceStatusFilter) =>
        `replacement-protocol-hint maintenance-filter-button maintenance-filter-${filter} border ${
            isMaintenanceFilterSelected(filter) ? 'is-selected' : ''
        }`;
    const getMaintenanceFilterStyle = (filter: MaintenanceStatusFilter): React.CSSProperties => {
        if (!isMaintenanceFilterSelected(filter)) {
            return {};
        }

        const styles: Record<MaintenanceStatusFilter, React.CSSProperties> = {
            pending: { backgroundColor: '#212529', borderColor: '#212529', color: '#fff' },
            paused: {
                backgroundColor: '#5c636a',
                borderColor: '#565e64',
                boxShadow: '0 0 0 .25rem rgba(130, 138, 145, .5)',
                color: '#fff',
            },
            serviced: { backgroundColor: '#198754', borderColor: '#198754', color: '#fff' },
            overdue: { backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff' },
            upcoming: { backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#212529' },
        };

        return styles[filter];
    };

    const getMaintenanceDaysStatus = (branch: DeviceCountBranchRow) => {
        const getDaysColor = () => {
            switch (branch.branchState) {
                case 'serviced':
                    return '#0f8f4f';
                case 'overdue':
                    return '#c82333';
                case 'upcoming':
                    return '#b58100';
                case 'paused':
                    return '#495057';
                default:
                    return '#0d6efd';
            }
        };
        const renderDays = (label: string, value: number) => (
            <>
                {label}:{' '}
                <span className='fw-bold' style={{ color: getDaysColor() }}>
                    {value}
                </span>
            </>
        );
        const maintenanceDate =
            branch.nextMaintenanceDate ??
            (typeof branch.nextMaintenanceDay === 'string' ? branch.nextMaintenanceDay : null) ??
            (typeof branch.nextMaintainceDay === 'string' ? branch.nextMaintainceDay : null);
        const responseDays =
            typeof branch.nextMaintenanceDay === 'number'
                ? branch.nextMaintenanceDay
                : typeof branch.nextMaintainceDay === 'number'
                ? branch.nextMaintainceDay
                : null;
        const days = maintenanceDate ? getDaysUntilDate(maintenanceDate) : responseDays;

        if (days === null) {
            return '';
        }

        if (days < 0) {
            const passedDays = Math.abs(days);
            const label = branch.branchState === 'serviced' ? t('elapsedDays') : t('overdueDays');

            return renderDays(label, passedDays);
        }

        if (days === 0) {
            return t('today');
        }

        return renderDays(t('remainingDays'), days);
    };
    const getMaintenanceDaysStatusText = (branch: DeviceCountBranchRow) => {
        const maintenanceDate =
            branch.nextMaintenanceDate ??
            (typeof branch.nextMaintenanceDay === 'string' ? branch.nextMaintenanceDay : null) ??
            (typeof branch.nextMaintainceDay === 'string' ? branch.nextMaintainceDay : null);
        const responseDays =
            typeof branch.nextMaintenanceDay === 'number'
                ? branch.nextMaintenanceDay
                : typeof branch.nextMaintainceDay === 'number'
                ? branch.nextMaintainceDay
                : null;
        const days = maintenanceDate ? getDaysUntilDate(maintenanceDate) : responseDays;

        if (days === null) {
            return '';
        }

        if (days < 0) {
            return `${branch.branchState === 'serviced' ? t('elapsedDays') : t('overdueDays')}: ${Math.abs(days)}`;
        }

        if (days === 0) {
            return t('today');
        }

        return `${t('remainingDays')}: ${days}`;
    };

    const sortedEssencePriceGroups = React.useMemo(
        () => [...essencePriceGroups].sort((a, b) => a.groupNumber - b.groupNumber),
        [essencePriceGroups]
    );
    const essencePriceGroupsByNumber = React.useMemo(
        () => new Map(sortedEssencePriceGroups.map((priceGroup) => [priceGroup.groupNumber, priceGroup])),
        [sortedEssencePriceGroups]
    );
    const formatPriceGroup = (priceGroupNumber: number) => {
        if (priceGroupNumber === UNKNOWN_PRICE_GROUP_NUMBER) {
            return t('withoutPrice');
        }

        const priceGroup = essencePriceGroupsByNumber.get(priceGroupNumber);
        return priceGroup ? `${t('group')} ${priceGroup.groupNumber}` : `${t('group')} ${priceGroupNumber}`;
    };
    const normalizeOilPriceKey = React.useCallback((price: string) => {
        const normalizedPrice = price.replace(',', '.').trim();
        const parsedPrice = Number(normalizedPrice);

        if (Number.isNaN(parsedPrice)) {
            return null;
        }

        return normalizedPrice.includes('.') ? Math.round(parsedPrice * 100) : parsedPrice;
    }, []);
    const filteredPricesByDistributor = React.useMemo(
        () =>
            (devicesPrices?.pricesByDistributor ?? []).filter(
                (distributor) => !selectedDistributor || distributor.distributorId === selectedDistributor
            ),
        [devicesPrices, selectedDistributor]
    );
    const priceGroupColumns = React.useMemo(() => {
        const priceGroupNumbers = new Set<number>();
        const addPriceGroup = (priceGroupNumber: number | null | undefined, devicesCount?: number) => {
            if (!devicesCount) {
                return;
            }

            if (priceGroupNumber !== undefined && priceGroupNumber !== null) {
                priceGroupNumbers.add(priceGroupNumber);
                return;
            }

            priceGroupNumbers.add(UNKNOWN_PRICE_GROUP_NUMBER);
        };

        filteredPricesByDistributor.forEach((distributor) => {
            Object.entries(distributor.devicesByOilPrice ?? {}).forEach(([oilPrice, devicesCount]) => {
                addPriceGroup(null, devicesCount);
            });

            if (distributor.activeDevicesWithoutOilPrice) {
                priceGroupNumbers.add(UNKNOWN_PRICE_GROUP_NUMBER);
            }

            Object.values(distributor.devicesByModel ?? {}).forEach((modelGroup) => {
                if (modelGroup.activeDevicesWithoutOilPrice) {
                    priceGroupNumbers.add(UNKNOWN_PRICE_GROUP_NUMBER);
                }

                (modelGroup.oils ?? []).forEach((oil) => {
                    addPriceGroup(oil.essencePriceGroupId, oil.devices);
                });
            });
        });

        const configuredColumns = sortedEssencePriceGroups
            .map((priceGroup) => priceGroup.groupNumber)
            .filter((priceGroupNumber): priceGroupNumber is number => priceGroupNumber !== undefined);
        const unknownColumns = priceGroupNumbers.has(UNKNOWN_PRICE_GROUP_NUMBER) ? [UNKNOWN_PRICE_GROUP_NUMBER] : [];
        const unconfiguredColumns = Array.from(priceGroupNumbers)
            .filter(
                (priceGroupNumber) =>
                    priceGroupNumber !== UNKNOWN_PRICE_GROUP_NUMBER && !configuredColumns.includes(priceGroupNumber)
            )
            .sort((a, b) => a - b);

        return [...configuredColumns, ...unconfiguredColumns, ...unknownColumns];
    }, [filteredPricesByDistributor, sortedEssencePriceGroups]);
    React.useEffect(() => {
        setSelectedPriceGroupFilters((currentPriceGroups) =>
            currentPriceGroups.filter((priceGroupNumber) => priceGroupColumns.includes(priceGroupNumber))
        );
    }, [priceGroupColumns]);
    const getPriceGroupColor = (priceGroupNumber: number) => {
        const priceGroup = essencePriceGroupsByNumber.get(priceGroupNumber);
        if (priceGroup?.color) {
            return priceGroup.color;
        }

        const priceIndex = Math.max(0, priceGroupColumns.indexOf(priceGroupNumber));

        return PRICE_COLORS[priceIndex % PRICE_COLORS.length];
    };
    const getSoftBadgeStyle = (color: string, minWidth?: number): React.CSSProperties => {
        return {
            backgroundColor: `${color}cc`,
            boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            gap: 4,
            lineHeight: '16px',
            minWidth,
        };
    };
    const formatBonus = (value: number) =>
        `${value.toLocaleString('bg-BG', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
        })} ${currencyConfiguration}`;
    const getPriceGroupBonusRate = (priceGroupNumber: number) =>
        essencePriceGroupsByNumber.get(priceGroupNumber)?.bonusRate ?? 0;
    const shouldIncludeBonusGroup = (priceGroupNumber: number) =>
        selectedPriceGroupFilters.length === 0 || selectedPriceGroupFilters.includes(priceGroupNumber);
    const getDistributorEarnedBonus = (distributor: DevicePricesByDistributorRow) => {
        let bonus = 0;

        Object.values(distributor.devicesByModel ?? {}).forEach((modelGroup) => {
            (modelGroup.oils ?? []).forEach((oil) => {
                const priceGroupNumber = oil.essencePriceGroupId ?? UNKNOWN_PRICE_GROUP_NUMBER;

                if (priceGroupNumber === UNKNOWN_PRICE_GROUP_NUMBER || !shouldIncludeBonusGroup(priceGroupNumber)) {
                    return;
                }

                bonus += (oil.devices ?? 0) * getPriceGroupBonusRate(priceGroupNumber);
            });
        });

        return bonus;
    };
    const getDistributorSalesBonus = (distributor: DevicePricesByDistributorRow) => Number(distributor.saleBonus ?? 0);
    const getDistributorSalesCount = (distributor: DevicePricesByDistributorRow) => Number(distributor.countSales ?? 0);
    const getDistributorSalesTotal = (distributor: DevicePricesByDistributorRow) => Number(distributor.salesTotal ?? 0);
    const getInstallationBonus = (distributor: DevicePricesByDistributorRow) => Number(distributor.installationBonus ?? 0);
    const getReedInstallationBonus = (distributor: DevicePricesByDistributorRow) => Number(distributor.reedInstallationBonus ?? 0);
    const getConsumableServiceBonus = (distributor: DevicePricesByDistributorRow) => Number(distributor.consumableServiceBonus ?? 0);
    const getDistributorTotalBonus = (distributor: DevicePricesByDistributorRow) =>
        getDistributorEarnedBonus(distributor) + getDistributorSalesBonus(distributor) + getInstallationBonus(distributor)
        + getReedInstallationBonus(distributor) + getConsumableServiceBonus(distributor);
    const earnedBonusTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getDistributorEarnedBonus(distributor),
        0
    );
    const salesBonusTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getDistributorSalesBonus(distributor),
        0
    );
    const salesCountTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getDistributorSalesCount(distributor),
        0
    );
    const salesAmountTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getDistributorSalesTotal(distributor),
        0
    );
    const installationBonusTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getInstallationBonus(distributor),
        0
    );
    const reedInstallationBonusTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getReedInstallationBonus(distributor), 0
    );
    const consumableServiceBonusTotal = filteredPricesByDistributor.reduce(
        (total, distributor) => total + getConsumableServiceBonus(distributor), 0
    );
    const totalBonusTotal = earnedBonusTotal + salesBonusTotal + installationBonusTotal
        + reedInstallationBonusTotal + consumableServiceBonusTotal;
    const getInstalledDevicesTooltip = (distributor: DevicePricesByDistributorRow) =>
        (distributor.installedDeviceGroups ?? [])
            .filter((group) => group.devices > 0)
            .map((group) => `гр. ${group.groupNumber ?? group.deviceGroupId ?? t('withoutDeviceGroup')}: ${group.devices} бр.`)
            .join('\n');
    const getInstalledReedsTooltip = (distributor: DevicePricesByDistributorRow) =>
        (distributor.installedReedGroups ?? [])
            .filter((group) => group.reeds > 0)
            .map((group) => `гр. ${group.groupNumber ?? group.groupId}: ${group.reeds} бр.`)
            .join('\n');
    const getServicedReedsTooltip = (distributor: DevicePricesByDistributorRow) =>
        (distributor.servicedConsumableGroups ?? [])
            .filter((group) => group.reeds > 0)
            .map((group) => `гр. ${group.groupNumber ?? group.groupId}: ${group.reeds} бр.`)
            .join('\n');
    const hasDistributorPriceSummary = filteredPricesByDistributor.length > 0;
    const deviceModelColumns = React.useMemo(() => {
        const models = new Set<string>();
        let hasLegacyPrices = false;

        filteredPricesByDistributor.forEach((distributor) => {
            Object.keys(distributor.devicesByModel ?? {}).forEach((model) => models.add(model));
            if (Object.keys(distributor.devicesByOilPrice ?? {}).length > 0 || distributor.activeDevicesWithoutOilPrice) {
                hasLegacyPrices = true;
            }
        });

        if (models.size === 0 && hasLegacyPrices) {
            models.add(t('total'));
        }

        return Array.from(models).sort((a, b) => a.localeCompare(b));
    }, [filteredPricesByDistributor, t]);
    const getDistributorModelPriceGroups = (distributor: DevicePricesByDistributorRow, model: string) => {
        const groups = new Map<number, number>();
        const addDevices = (priceGroupNumber: number, devicesCount: number) => {
            if (!devicesCount) {
                return;
            }

            groups.set(priceGroupNumber, (groups.get(priceGroupNumber) ?? 0) + devicesCount);
        };
        const modelGroup = distributor.devicesByModel?.[model];

        if (modelGroup) {
            (modelGroup.oils ?? []).forEach((oil) => {
                const priceGroupNumber = oil.essencePriceGroupId ?? UNKNOWN_PRICE_GROUP_NUMBER;

                if (priceGroupColumns.includes(priceGroupNumber)) {
                    addDevices(priceGroupNumber, oil.devices ?? 0);
                }
            });

            addDevices(UNKNOWN_PRICE_GROUP_NUMBER, modelGroup.activeDevicesWithoutOilPrice ?? 0);
        }

        if (!modelGroup && model === t('total')) {
            Object.entries(distributor.devicesByOilPrice ?? {}).forEach(([oilPrice, devicesCount]) => {
                if (normalizeOilPriceKey(oilPrice) !== null && priceGroupColumns.includes(UNKNOWN_PRICE_GROUP_NUMBER)) {
                    addDevices(UNKNOWN_PRICE_GROUP_NUMBER, devicesCount);
                }
            });

            addDevices(UNKNOWN_PRICE_GROUP_NUMBER, distributor.activeDevicesWithoutOilPrice ?? 0);
        }

        return Array.from(groups.entries())
            .map(([priceGroupNumber, quantity]) => ({
                priceGroupNumber,
                quantity,
            }))
            .sort(
                (a, b) =>
                    priceGroupColumns.indexOf(a.priceGroupNumber) - priceGroupColumns.indexOf(b.priceGroupNumber)
            );
    };
    const getModelPriceGroupsTotal = (model: string) => {
        const groups = new Map<number, number>();

        filteredPricesByDistributor.forEach((distributor) => {
            getDistributorModelPriceGroups(distributor, model).forEach((group) => {
                groups.set(group.priceGroupNumber, (groups.get(group.priceGroupNumber) ?? 0) + group.quantity);
            });
        });

        return Array.from(groups.entries())
            .map(([priceGroupNumber, quantity]) => ({
                priceGroupNumber,
                quantity,
            }))
            .sort(
                (a, b) =>
                    priceGroupColumns.indexOf(a.priceGroupNumber) - priceGroupColumns.indexOf(b.priceGroupNumber)
            );
    };
    const renderModelPriceGroups = (groups: ReturnType<typeof getDistributorModelPriceGroups>) => {
        const visibleGroups =
            selectedPriceGroupFilters.length === 0
                ? groups
                : groups.filter((group) => selectedPriceGroupFilters.includes(group.priceGroupNumber));

        if (visibleGroups.length === 0) {
            return '';
        }

        return (
            <div className='d-flex flex-wrap justify-content-center gap-1' data-export-price-groups='true'>
                {visibleGroups.map((group) => {
                    const priceGroupColor = getPriceGroupColor(group.priceGroupNumber);

                    return (
                        <span
                            key={group.priceGroupNumber}
                            title={formatPriceGroup(group.priceGroupNumber)}
                            className='d-inline-flex justify-content-center align-items-center rounded px-1'
                            style={getSoftBadgeStyle(priceGroupColor, 24)}
                            data-export-price-color={priceGroupColor}
                        >
                            <span>{group.quantity}</span>
                        </span>
                    );
                })}
            </div>
        );
    };
    const renderPriceColorLegend = () => (
        <div className='d-flex flex-wrap justify-content-end gap-1 ms-3'>
            {priceGroupColumns.map((priceGroupNumber) => {
                const isSelected = selectedPriceGroupFilters.includes(priceGroupNumber);

                return (
                    <button
                        key={priceGroupNumber}
                        type='button'
                        className='d-inline-flex align-items-center rounded border-0 px-1'
                        onClick={() =>
                            setSelectedPriceGroupFilters((currentPriceGroups) =>
                                currentPriceGroups.includes(priceGroupNumber)
                                    ? currentPriceGroups.filter(
                                          (currentPriceGroup) => currentPriceGroup !== priceGroupNumber
                                      )
                                    : [...currentPriceGroups, priceGroupNumber]
                            )
                        }
                        style={{
                            ...getSoftBadgeStyle(getPriceGroupColor(priceGroupNumber)),
                            cursor: 'pointer',
                            opacity: selectedPriceGroupFilters.length === 0 || isSelected ? 1 : 0.45,
                            outline: isSelected ? '2px solid rgba(255, 255, 255, 0.9)' : undefined,
                            outlineOffset: 1,
                        }}
                        title={formatPriceGroup(priceGroupNumber)}
                    >
                        {priceGroupNumber === UNKNOWN_PRICE_GROUP_NUMBER
                            ? '-'
                            : essencePriceGroupsByNumber.get(priceGroupNumber)?.groupNumber ?? priceGroupNumber}
                    </button>
                );
            })}
        </div>
    );
    const formatDate = (value?: string | null) => {
        if (!value) {
            return null;
        }

        return Number.isNaN(new Date(value).getTime()) ? value : dateFormat(value, 'dd-mm-yyyy');
    };

    const getPausedUntil = (device: DeviceCountDevice) => {
        if (device.active) {
            return '';
        }

        return formatDate(device.pausedUntil) ?? '';
    };

    const getDeviceModelClassName = (device: DeviceCountDevice) => {
        switch (device.operation) {
            case 'RETURNED':
                return 'text-danger fw-bold';
            case 'ACTIVATED':
                return 'text-success fw-bold';
            case 'INSTALLED':
                return 'text-success fw-bold';
            case 'DEACTIVATED':
                return 'fw-bold';
            default:
                return device.active ? 'text-success fw-bold' : 'text-danger fw-bold';
        }
    };

    const getDeviceModelStyle = (device: DeviceCountDevice): React.CSSProperties => {
        if (device.operation === 'DEACTIVATED') {
            return { color: '#4d5155' };
        }

        return {};
    };
    const getReplacementReturnedDevices = (
        row: Pick<
            DeviceSummaryResponse,
            'replacementReturnedDevices' | 'returnedReplacementDevices' | 'returnedDevicesWithInstalledInLast30Days'
        >
    ) =>
        row.replacementReturnedDevices ??
        row.returnedReplacementDevices ??
        row.returnedDevicesWithInstalledInLast30Days ??
        0;
    const deviceSummaryModelTotals = (devicesSummary?.models ?? []).reduce<{
        totalDevices: number;
        installedDevices: number;
        replacementReturnedDevices: number;
        returnedDevices: number;
        activeDevices: number;
        pausedDevices: number;
        servicedDevices: number;
    }>(
        (totals, row) => ({
            totalDevices: totals.totalDevices + (row.totalDevices ?? 0),
            installedDevices: totals.installedDevices + (row.installedDevices ?? 0),
            replacementReturnedDevices:
                totals.replacementReturnedDevices + getReplacementReturnedDevices(row),
            returnedDevices: totals.returnedDevices + (row.returnedDevices ?? 0),
            activeDevices: totals.activeDevices + (row.activeDevices ?? 0),
            pausedDevices: totals.pausedDevices + (row.pausedDevices ?? 0),
            servicedDevices: totals.servicedDevices + (row.servicedDevices ?? 0),
        }),
        {
            totalDevices: 0,
            installedDevices: 0,
            replacementReturnedDevices: 0,
            returnedDevices: 0,
            activeDevices: 0,
            pausedDevices: 0,
            servicedDevices: 0,
        }
    );
    const netInstalledDevices =
        deviceSummaryModelTotals.installedDevices - deviceSummaryModelTotals.replacementReturnedDevices;
    const renderInstalledReplacementDevices = (installedDevices?: number, replacementReturnedDevices = 0) => {
        const installed = installedDevices ?? 0;

        if (!replacementReturnedDevices) {
            return <span>{installed}</span>;
        }

        return (
            <span>
                <span>{installed}</span>
                <span> / </span>
                <span className='text-danger'>{replacementReturnedDevices}</span>
            </span>
        );
    };
    const rgbToHex = (value: number) => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0');
    const normalizeExcelColor = (value: string) => {
        const shortHex = value.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);

        if (shortHex) {
            return `#${shortHex[1]}${shortHex[1]}${shortHex[2]}${shortHex[2]}${shortHex[3]}${shortHex[3]}`;
        }

        const hexWithAlpha = value.match(/#([0-9a-f]{6})[0-9a-f]{2}/i);

        if (hexWithAlpha) {
            return `#${hexWithAlpha[1]}`;
        }

        const rgba = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);

        if (rgba) {
            return `#${rgbToHex(Number(rgba[1]))}${rgbToHex(Number(rgba[2]))}${rgbToHex(Number(rgba[3]))}`;
        }

        return value;
    };
    const getExcelArgbColor = (value: string) => {
        const normalized = normalizeExcelColor(value).replace('#', '').toUpperCase();

        return /^[0-9A-F]{6}$/.test(normalized) ? `FF${normalized}` : 'FF000000';
    };
    const getPriceGroupsRichText = (groups: ReturnType<typeof getDistributorModelPriceGroups>) => {
        const visibleGroups =
            selectedPriceGroupFilters.length === 0
                ? groups
                : groups.filter((group) => selectedPriceGroupFilters.includes(group.priceGroupNumber));

        return visibleGroups.flatMap((group, index) => [
            ...(index > 0 ? [{ text: ' ' }] : []),
            {
                text: String(group.quantity),
                font: {
                    bold: true,
                    color: { argb: getExcelArgbColor(getPriceGroupColor(group.priceGroupNumber)) },
                },
            },
        ]);
    };
    const getInstalledReplacementRichText = (installedDevices?: number, replacementReturnedDevices = 0) => {
        const installed = installedDevices ?? 0;

        if (!replacementReturnedDevices) {
            return [{ text: String(installed) }];
        }

        return [
            { text: String(installed) },
            { text: ' / ' },
            { text: String(replacementReturnedDevices), font: { color: { argb: 'FFDC3545' } } },
        ];
    };
    const applyExcelCellStyle = (cell: ExcelJS.Cell, options?: { header?: boolean; title?: boolean; total?: boolean }) => {
        cell.border = {};
        cell.alignment = { vertical: 'middle', horizontal: options?.title ? 'left' : 'center', wrapText: true };
        cell.font = { name: 'Arial', size: 11, bold: options?.header || options?.title || options?.total };

        if (options?.title) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF212529' } };
            cell.font = { name: 'Arial', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        } else if (options?.header) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F5F7' } };
        } else if (options?.total) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8F9FA' } };
        }
    };
    const getBranchExcelFill = (branch: DeviceCountBranchRow) => {
        const colors: Record<string, string> = {
            overdue: 'FFF8D7DA',
            paused: 'FFE2E3E5',
            serviced: 'FFD1E7DD',
            upcoming: 'FFFFF3CD',
        };

        return branch.branchState ? colors[branch.branchState] : undefined;
    };
    const getDeviceExcelColor = (device: DeviceCountDevice) => {
        const styleColor = getDeviceModelStyle(device).color;

        return getExcelArgbColor(typeof styleColor === 'string' ? styleColor : device.active ? '#198754' : '#dc3545');
    };
    const getTextRowHeight = (values: Array<string | null | undefined>, baseHeight = 21) => {
        const maxLength = Math.max(0, ...values.map((value) => String(value ?? '').length));
        const wrappedLines = Math.ceil(maxLength / 24);

        return Math.max(baseHeight, Math.min(120, wrappedLines * 18));
    };
    const applyDeviceGroupExcelStyle = (
        worksheet: ExcelJS.Worksheet,
        startRow: number,
        endRow: number,
        modelColor?: string
    ) => {
        for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
            const row = worksheet.getRow(rowIndex);

            for (let columnIndex = 4; columnIndex <= 7; columnIndex += 1) {
                const cell = row.getCell(columnIndex);
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F7F5' } };
                cell.alignment = {
                    ...cell.alignment,
                    horizontal: columnIndex === 4 || columnIndex === 5 ? 'left' : 'center',
                    indent: columnIndex === 4 || columnIndex === 5 ? 1 : 0,
                    vertical: 'middle',
                    wrapText: true,
                };
            }
        }

        if (modelColor) {
            const modelCell = worksheet.getRow(startRow).getCell(4);
            modelCell.font = {
                name: 'Arial',
                size: 11,
                bold: true,
                color: { argb: modelColor },
            };
        }

        applyExcelOutlineBorder(worksheet, startRow, endRow, 4, 7, 'FFE3E7EC');
    };
    const applyExcelOutlineBorder = (
        worksheet: ExcelJS.Worksheet,
        startRow: number,
        endRow: number,
        startColumn: number,
        endColumn: number,
        color: string
    ) => {
        for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
            const row = worksheet.getRow(rowIndex);

            for (let columnIndex = startColumn; columnIndex <= endColumn; columnIndex += 1) {
                const cell = row.getCell(columnIndex);
                cell.border = {
                    ...cell.border,
                    top:
                        rowIndex === startRow
                            ? { style: 'thin', color: { argb: color } }
                            : cell.border?.top,
                    bottom:
                        rowIndex === endRow
                            ? { style: 'thin', color: { argb: color } }
                            : cell.border?.bottom,
                    left:
                        columnIndex === startColumn
                            ? { style: 'thin', color: { argb: color } }
                            : cell.border?.left,
                    right:
                        columnIndex === endColumn
                            ? { style: 'thin', color: { argb: color } }
                            : cell.border?.right,
                };
            }
        }
    };
    const applyBranchLabelExcelStyle = (
        worksheet: ExcelJS.Worksheet,
        startRow: number,
        endRow: number,
        values: Array<string | null | undefined>
    ) => {
        if (endRow > startRow) {
            worksheet.mergeCells(startRow, 1, endRow, 1);
            worksheet.mergeCells(startRow, 2, endRow, 2);
            worksheet.mergeCells(startRow, 3, endRow, 3);
        }

        const requiredHeight = getTextRowHeight(values);
        const currentHeight = Array.from({ length: endRow - startRow + 1 }, (_, index) => {
            return worksheet.getRow(startRow + index).height ?? 21;
        }).reduce((total, height) => total + height, 0);

        if (currentHeight < requiredHeight) {
            const firstRow = worksheet.getRow(startRow);
            firstRow.height = (firstRow.height ?? 21) + requiredHeight - currentHeight;
        }

        for (let columnIndex = 1; columnIndex <= 3; columnIndex += 1) {
            const cell = worksheet.getRow(startRow).getCell(columnIndex);
            cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
        }
    };
    const exportDevicesTable = async () => {
        try {
            setDevicesExportLoading(true);
            const rows = await fetchDevicesForExport();
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet(t('devices').slice(0, 31));
            const headers = [
                t('companyName'),
                t('branch'),
                t('maintenanceDaysStatus'),
                t('devices'),
                t('distributor'),
                t('date'),
                t('pausedInterval'),
            ];
            let currentRow = 1;

            worksheet.columns = [
                { width: 28 },
                { width: 28 },
                { width: 24 },
                { width: 22 },
                { width: 24 },
                { width: 14 },
                { width: 20 },
            ];

            const titleRow = worksheet.getRow(currentRow);
            titleRow.getCell(1).value = `${t('devices')}: ${summaryFilterText}`;
            worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
            applyExcelCellStyle(titleRow.getCell(1), { title: true });
            titleRow.height = 22;
            currentRow += 1;

            const headerRow = worksheet.getRow(currentRow);
            headers.forEach((header, index) => {
                const cell = headerRow.getCell(index + 1);
                cell.value = header;
                applyExcelCellStyle(cell, { header: true });
            });
            headerRow.height = 24;
            currentRow += 1;

            if (rows.length === 0) {
                const row = worksheet.getRow(currentRow);
                row.getCell(1).value = t('noData');
                worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
                applyExcelCellStyle(row.getCell(1));
            }

            rows.forEach((branch, branchIndex) => {
                const branchStartRow = currentRow;
                const deviceGroups: Array<[number, Array<DeviceCountDevice | null>]> =
                    branch.devices && branch.devices.length > 0 ? groupDevicesById(branch.devices) : [[0, [null]]];
                const fillColor = getBranchExcelFill(branch);
                let isFirstBranchRow = true;

                deviceGroups.forEach(([, devices], deviceGroupIndex) => {
                    const deviceStartRow = currentRow;
                    const firstDevice = devices.find((device): device is DeviceCountDevice => device !== null);

                    devices.forEach((device, deviceIndex) => {
                        const row = worksheet.getRow(currentRow);
                        const distributorName = device
                            ? branch.assignedDistributorName ??
                              distributorsById.get(device.changedByUserId ?? branch.assignedDistributor ?? 0) ??
                              ''
                            : branch.assignedDistributorName ?? '';

                        row.values = [
                            isFirstBranchRow ? getBranchCompanyName(branch) : '',
                            isFirstBranchRow ? branch.name ?? '' : '',
                            isFirstBranchRow ? getMaintenanceDaysStatusText(branch) : '',
                            device && deviceIndex === 0 ? device.model ?? device.deviceName ?? '' : '',
                            distributorName,
                            device?.changedAt ? dateFormat(device.changedAt, 'dd-mm-yyyy') : '',
                            device ? getPausedUntil(device) : '',
                        ];

                        for (let cellIndex = 1; cellIndex <= headers.length; cellIndex += 1) {
                            const cell = row.getCell(cellIndex);
                            applyExcelCellStyle(cell);
                            if (cellIndex === 1 || cellIndex === 2) {
                                cell.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
                            }
                            if (fillColor) {
                                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
                            }
                        }

                        isFirstBranchRow = false;
                        row.height = 21;
                        currentRow += 1;
                    });

                    if (devices.length > 1) {
                        worksheet.mergeCells(deviceStartRow, 4, currentRow - 1, 4);
                    }
                    applyDeviceGroupExcelStyle(
                        worksheet,
                        deviceStartRow,
                        currentRow - 1,
                        firstDevice ? getDeviceExcelColor(firstDevice) : undefined
                    );

                    if (deviceGroupIndex < deviceGroups.length - 1) {
                        const spacerRow = worksheet.getRow(currentRow);
                        spacerRow.height = 5;
                        for (let cellIndex = 1; cellIndex <= headers.length; cellIndex += 1) {
                            const cell = spacerRow.getCell(cellIndex);
                            applyExcelCellStyle(cell);
                            if (fillColor) {
                                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
                            }
                        }
                        currentRow += 1;
                    }
                });

                applyBranchLabelExcelStyle(worksheet, branchStartRow, currentRow - 1, [
                    getBranchCompanyName(branch),
                    branch.name,
                    getMaintenanceDaysStatusText(branch),
                ]);
                applyExcelOutlineBorder(worksheet, branchStartRow, currentRow - 1, 1, headers.length, 'FFBFC5CC');

                if (branchIndex < rows.length - 1) {
                    worksheet.getRow(currentRow).height = 5;
                    currentRow += 1;
                }
            });

            worksheet.views = [{ state: 'frozen', ySplit: 2, showGridLines: false }];
            const buffer = await workbook.xlsx.writeBuffer();
            const data = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
            });

            FileSaver.saveAs(data, `devices-${dateFormat(new Date(), 'yyyy-mm-dd')}.xlsx`);
        } catch (err) {
            console.error('Error exporting devices table', err);
        } finally {
            setDevicesExportLoading(false);
        }
    };
    const exportDevicesPricesTable = async () => {
        try {
            setPricesExportLoading(true);

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet(t('summary').slice(0, 31));
            const summaryColumnCount = 7;
            const pricesColumnCount = deviceModelColumns.length + 7;
            const columnCount = Math.max(summaryColumnCount, pricesColumnCount);
            let currentRow = 1;

            worksheet.columns = Array.from({ length: columnCount }, (_, index) => ({
                width: index === 0 ? 22 : index <= deviceModelColumns.length ? 11 : 17,
            }));

            const addTitleRow = (title: string, columns: number) => {
                const row = worksheet.getRow(currentRow);
                row.getCell(1).value = title;
                worksheet.mergeCells(currentRow, 1, currentRow, columns);
                applyExcelCellStyle(row.getCell(1), { title: true });
                row.height = 22;
                currentRow += 1;
            };
            const addHeaderRow = (headers: string[]) => {
                const row = worksheet.getRow(currentRow);
                headers.forEach((header, index) => {
                    const cell = row.getCell(index + 1);
                    cell.value = header;
                    applyExcelCellStyle(cell, { header: true });
                });
                row.height = 24;
                currentRow += 1;
            };
            const styleRow = (row: ExcelJS.Row, cellsCount: number, total = false) => {
                for (let index = 1; index <= cellsCount; index += 1) {
                    applyExcelCellStyle(row.getCell(index), { total });
                }
            };

            addTitleRow(`${t('summary')}: ${summaryFilterText}`, summaryColumnCount);
            addHeaderRow([
                t('model'),
                t('devices'),
                t('installedReplacementDevices'),
                t('returnedDevices'),
                t('activeDevices'),
                t('pausedDevices'),
                t('servicedDevices'),
            ]);
            (devicesSummary?.models ?? []).forEach((summaryRow) => {
                const row = worksheet.getRow(currentRow);
                row.values = [
                    summaryRow.model ?? '',
                    summaryRow.totalDevices,
                    '',
                    summaryRow.returnedDevices ?? 0,
                    summaryRow.activeDevices,
                    summaryRow.pausedDevices,
                    summaryRow.servicedDevices ?? 0,
                ];
                row.getCell(3).value = {
                    richText: getInstalledReplacementRichText(
                        summaryRow.installedDevices,
                        getReplacementReturnedDevices(summaryRow)
                    ),
                };
                styleRow(row, summaryColumnCount);
                currentRow += 1;
            });

            const summaryTotalRow = worksheet.getRow(currentRow);
            summaryTotalRow.values = [
                t('totalByModels'),
                deviceSummaryModelTotals.totalDevices,
                netInstalledDevices,
                deviceSummaryModelTotals.returnedDevices,
                deviceSummaryModelTotals.activeDevices,
                deviceSummaryModelTotals.pausedDevices,
                deviceSummaryModelTotals.servicedDevices,
            ];
            styleRow(summaryTotalRow, summaryColumnCount, true);
            currentRow += 2;

            addTitleRow(`${t('pricesByDistributor')}: ${pricesFilterText}`, pricesColumnCount);
            addHeaderRow([
                t('distributor'),
                ...deviceModelColumns,
                t('installationBonus'),
                t('earnedBonus'),
                t('reedInstallationBonus'),
                t('consumableServiceBonus'),
                t('salesBonus'),
                t('totalBonuses'),
            ]);
            filteredPricesByDistributor.forEach((distributor) => {
                const row = worksheet.getRow(currentRow);

                row.getCell(1).value = distributor.distributorName ?? '';
                deviceModelColumns.forEach((model, index) => {
                    const richText = getPriceGroupsRichText(getDistributorModelPriceGroups(distributor, model));

                    row.getCell(index + 2).value = richText.length ? { richText } : '';
                });
                row.getCell(deviceModelColumns.length + 2).value = `${formatBonus(getInstallationBonus(distributor))}\n${
                    distributor.installedDevices ?? 0
                } ${t('pcs')}`;
                row.getCell(deviceModelColumns.length + 3).value = formatBonus(getDistributorEarnedBonus(distributor));
                row.getCell(deviceModelColumns.length + 4).value = `${formatBonus(getReedInstallationBonus(distributor))}\n${distributor.installedReeds ?? 0} ${t('pcs')}`;
                row.getCell(deviceModelColumns.length + 5).value = `${formatBonus(getConsumableServiceBonus(distributor))}\n${distributor.servicedReeds ?? 0} ${t('pcs')}`;
                row.getCell(deviceModelColumns.length + 6).value = `${formatBonus(getDistributorSalesBonus(distributor))}\n${formatBonus(
                    getDistributorSalesTotal(distributor)
                )} / ${getDistributorSalesCount(distributor)} ${t('salesCountShort')}`;
                row.getCell(deviceModelColumns.length + 7).value = formatBonus(getDistributorTotalBonus(distributor));
                styleRow(row, pricesColumnCount);
                currentRow += 1;
            });

            const pricesTotalRow = worksheet.getRow(currentRow);
            pricesTotalRow.getCell(1).value = t('priceSummaryTotal');
            deviceModelColumns.forEach((model, index) => {
                const richText = getPriceGroupsRichText(getModelPriceGroupsTotal(model));

                pricesTotalRow.getCell(index + 2).value = richText.length ? { richText } : '';
            });
            pricesTotalRow.getCell(deviceModelColumns.length + 2).value = `${formatBonus(
                installationBonusTotal
            )}\n${filteredPricesByDistributor.reduce(
                (total, distributor) => total + (distributor.installedDevices ?? 0),
                0
            )} ${t('pcs')}`;
            pricesTotalRow.getCell(deviceModelColumns.length + 3).value = formatBonus(earnedBonusTotal);
            pricesTotalRow.getCell(deviceModelColumns.length + 4).value = formatBonus(reedInstallationBonusTotal);
            pricesTotalRow.getCell(deviceModelColumns.length + 5).value = formatBonus(consumableServiceBonusTotal);
            pricesTotalRow.getCell(deviceModelColumns.length + 6).value = `${formatBonus(salesBonusTotal)}\n${formatBonus(
                salesAmountTotal
            )} / ${salesCountTotal} ${t('salesCountShort')}`;
            pricesTotalRow.getCell(deviceModelColumns.length + 7).value = formatBonus(totalBonusTotal);
            styleRow(pricesTotalRow, pricesColumnCount, true);

            const buffer = await workbook.xlsx.writeBuffer();
            const data = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
            });

            FileSaver.saveAs(data, `devices-summary-prices-${dateFormat(new Date(), 'yyyy-mm-dd')}.xlsx`);
        } catch (err) {
            console.error('Error exporting visible devices summary and prices tables', err);
        } finally {
            setPricesExportLoading(false);
        }
    };

    return (
        <div className='w-100 pb-4 pt-2'>
            <div className='w-100 d-flex justify-content-between align-items-end px-3 mb-3'>
                <div className='d-flex flex-column'>
                    <Form.Label>{t('itemsToShow')}</Form.Label>
                    <Select
                        className='w-100'
                        value={{ label: pageSize, value: pageSize }}
                        options={[
                            { label: 10, value: 10 },
                            { label: 20, value: 20 },
                            { label: 50, value: 50 },
                            { label: 100, value: 100 },
                        ]}
                        onChange={(value) => {
                            setPagination({ pageSize: value?.value ?? 10, pageStart: 0 });
                            setCurrentPage(1);
                        }}
                    />
                </div>
                <Button
                    type='button'
                    variant='success'
                    className='mb-0'
                    disabled={exportLoading || devicesExportLoading}
                    onClick={exportDevicesTable}
                >
                    {(exportLoading || devicesExportLoading) && <Spinner animation='border' size='sm' className='me-2' />}
                    {t('exportExcel')}
                </Button>
            </div>
            <div className='w-100'>
                <Row className='customers-device-filters w-100 d-flex align-items-center justify-content-center px-3'>
                    <Col xs={12} md='auto' className='customers-device-filter-col mb-3'>
                        <Form.Label>{t('distributor')}</Form.Label>
                        <Select
                            className='w-100'
                            classNamePrefix='customers-device-select'
                            isClearable
                            options={distributors.items}
                            getOptionLabel={(d) => d.assignedDistributorName || d.username}
                            getOptionValue={(d) => String(d.id)}
                            onChange={(value) => setSelectedDistributor(value?.id ?? null)}
                        />
                    </Col>

                    <Col xs={12} md='auto' className='customers-device-filter-col mb-3'>
                        <Form.Label>{t('searchButton')}</Form.Label>
                        <Form.Control
                            type='text'
                            placeholder='Модел, Име, Обект'
                            className='w-100'
                            value={selectedDeviceModel ?? ''}
                            onChange={(e) => {
                                setSelectedDeviceModel(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </Col>

                    <Col xs={12} md='auto' className='mb-3'>
                        <Form.Label>{t('period')}</Form.Label>
                        <div className='customers-device-period-filter d-flex'>
                            <div className='customers-device-period-date'>
                                <small className='text-muted'>{t('fromDate')}</small>
                                <DatePickerWrapper
                                    selected={selectedFromDate}
                                    maxDate={selectedToDate}
                                    onChange={(value) => {
                                        setSelectedFromDate(value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <div className='customers-device-period-date'>
                                <small className='text-muted'>{t('toDate')}</small>
                                <DatePickerWrapper
                                    selected={selectedToDate}
                                    minDate={selectedFromDate}
                                    onChange={(value) => {
                                        setSelectedToDate(value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
            <div className='px-3 mt-4'>
                <div className='d-flex flex-wrap gap-2 justify-content-center mb-3'>
                    <Button
                        type='button'
                        variant={isMaintenanceFilterSelected('pending') ? 'dark' : 'light'}
                        size='sm'
                        className={getMaintenanceFilterClassName('pending')}
                        style={getMaintenanceFilterStyle('pending')}
                        active={isMaintenanceFilterSelected('pending')}
                        aria-pressed={isMaintenanceFilterSelected('pending')}
                        onClick={() => {
                            setSelectedMaintenanceSort((current) => (current === 'pending' ? null : 'pending'));
                            setCurrentPage(1);
                        }}
                    >
                        <span
                            className='replacement-protocol-marker'
                            style={{ backgroundColor: '#dee6ec', boxShadow: 'inset 0 0 0 2px #e9ecef' }}
                        />
                        <span>{t('withoutMaintenanceStatus')}</span>
                    </Button>
                    <Button
                        type='button'
                        variant={isMaintenanceFilterSelected('paused') ? 'secondary' : 'light'}
                        size='sm'
                        className={getMaintenanceFilterClassName('paused')}
                        style={getMaintenanceFilterStyle('paused')}
                        active={isMaintenanceFilterSelected('paused')}
                        aria-pressed={isMaintenanceFilterSelected('paused')}
                        onClick={() => {
                            setSelectedMaintenanceSort((current) => (current === 'paused' ? null : 'paused'));
                            setCurrentPage(1);
                        }}
                    >
                        <span
                            className='replacement-protocol-marker'
                            style={{ backgroundColor: '#6c757d', boxShadow: 'inset 0 0 0 2px #e2e3e5' }}
                        />
                        <span>{t('pausedObjects')}</span>
                    </Button>
                    <Button
                        type='button'
                        variant={isMaintenanceFilterSelected('serviced') ? 'success' : 'light'}
                        size='sm'
                        className={getMaintenanceFilterClassName('serviced')}
                        style={getMaintenanceFilterStyle('serviced')}
                        active={isMaintenanceFilterSelected('serviced')}
                        aria-pressed={isMaintenanceFilterSelected('serviced')}
                        onClick={() => {
                            setSelectedMaintenanceSort((current) => (current === 'serviced' ? null : 'serviced'));
                            setCurrentPage(1);
                        }}
                    >
                        <span
                            className='replacement-protocol-marker'
                            style={{ backgroundColor: '#198754', boxShadow: 'inset 0 0 0 2px #d1e7dd' }}
                        />
                        <span>{t('servicedThisMonth')}</span>
                    </Button>
                    <Button
                        type='button'
                        variant={isMaintenanceFilterSelected('overdue') ? 'danger' : 'light'}
                        size='sm'
                        className={getMaintenanceFilterClassName('overdue')}
                        style={getMaintenanceFilterStyle('overdue')}
                        active={isMaintenanceFilterSelected('overdue')}
                        aria-pressed={isMaintenanceFilterSelected('overdue')}
                        onClick={() => {
                            setSelectedMaintenanceSort((current) => (current === 'overdue' ? null : 'overdue'));
                            setCurrentPage(1);
                        }}
                    >
                        <span
                            className='replacement-protocol-marker'
                            style={{ backgroundColor: '#dc3545', boxShadow: 'inset 0 0 0 2px #f8d7da' }}
                        />
                        <span>{t('missedMaintenance')}</span>
                    </Button>
                    <Button
                        type='button'
                        variant={isMaintenanceFilterSelected('upcoming') ? 'warning' : 'light'}
                        size='sm'
                        className={getMaintenanceFilterClassName('upcoming')}
                        style={getMaintenanceFilterStyle('upcoming')}
                        active={isMaintenanceFilterSelected('upcoming')}
                        aria-pressed={isMaintenanceFilterSelected('upcoming')}
                        onClick={() => {
                            setSelectedMaintenanceSort((current) => (current === 'upcoming' ? null : 'upcoming'));
                            setCurrentPage(1);
                        }}
                    >
                        <span
                            className='replacement-protocol-marker'
                            style={{ backgroundColor: '#f0ad00', boxShadow: 'inset 0 0 0 2px #fff3cd' }}
                        />
                        <span>{t('upcomingMaintenanceInFiveDays')}</span>
                    </Button>
                </div>
                <Table bordered hover responsive>
                    <thead>
                        <tr>
                            <th>{t('companyName')}</th>
                            <th>{t('branch')}</th>
                            <th>{t('maintenanceDaysStatus')}</th>
                            <th>
                                <div
                                    className='d-grid gap-2'
                                    style={{
                                        gridTemplateColumns: 'minmax(120px, 1fr) minmax(140px, 1fr) 100px 150px',
                                    }}
                                >
                                    <span>{t('devices')}</span>
                                    <span>{t('distributor')}</span>
                                    <span>{t('date')}</span>
                                    <span>{t('pausedInterval')}</span>
                                </div>
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {devicesLoading ? (
                            <tr>
                                <td colSpan={4} className='text-center py-4'>
                                    <Spinner animation='border' size='sm' className='me-2' />
                                    <span>{t('loading')}</span>
                                </td>
                            </tr>
                        ) : sortedDevices && sortedDevices.length > 0 ? (
                            <>
                                {sortedDevices.map((d) => (
                                    <tr key={d.id} style={getMaintenanceRowStyle(d)} onClick={() => FetchData(d)}>
                                        <td>{getBranchCompanyName(d)}</td>
                                        <td>{d.name ?? ''}</td>
                                        <td>{getMaintenanceDaysStatus(d)}</td>
                                        <td>
                                            {(d.devices?.length ?? 0) > 0 || (d.reeds?.length ?? 0) > 0 ? (
                                                <div className='d-flex flex-column gap-2'>
                                                    {groupDevicesById(d.devices ?? []).map(([deviceId, groupedDevices]) => (
                                                        <div
                                                            key={deviceId}
                                                            className='border rounded px-2 py-1'
                                                            style={{
                                                                borderColor: '#dee2e6',
                                                                backgroundColor: 'rgba(255, 255, 255, 0.45)',
                                                            }}
                                                        >
                                                            {groupedDevices.map((device) => {
                                                                const distributorName =
                                                                    d.assignedDistributorName ??
                                                                    distributorsById.get(
                                                                        device.changedByUserId ??
                                                                            d.assignedDistributor ??
                                                                            0
                                                                    ) ?? '';

                                                                return (
                                                                    <div
                                                                        key={device.historyId ?? device.id}
                                                                        className='d-grid gap-2 align-items-center'
                                                                        style={{
                                                                            gridTemplateColumns:
                                                                                'minmax(120px, 1fr) minmax(140px, 1fr) 100px 150px',
                                                                        }}
                                                                    >
                                                                        <span
                                                                            className={getDeviceModelClassName(device)}
                                                                            style={getDeviceModelStyle(device)}
                                                                        >
                                                                            {device.model ?? device.deviceName ?? ''}
                                                                        </span>
                                                                        <span>{distributorName}</span>
                                                                        <span>
                                                                            {device.changedAt
                                                                                ? dateFormat(
                                                                                      device.changedAt,
                                                                                      'dd-mm-yyyy'
                                                                                  )
                                                                                : ''}
                                                                        </span>
                                                                        <span>{getPausedUntil(device)}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                    {(d.reeds ?? []).map((reed) => (
                                                        <div
                                                            key={`reed-group-${reed.id}`}
                                                            className='border rounded px-2 py-1'
                                                            style={{
                                                                borderColor: '#dee2e6',
                                                                backgroundColor: 'rgba(255, 255, 255, 0.45)',
                                                            }}
                                                        >
                                                            {Array.from({ length: Math.max(reed.count, 1) }, (_, index) => (
                                                                <div
                                                                    key={`reed-${reed.id}-${index}`}
                                                                    className='d-grid gap-2 align-items-center'
                                                                    style={{
                                                                        gridTemplateColumns:
                                                                            'minmax(120px, 1fr) minmax(140px, 1fr) 100px 150px',
                                                                    }}
                                                                >
                                                                    <span className='text-success fw-bold' style={{ color: '#198754' }}>
                                                                        {reed.model ?? ''}
                                                                    </span>
                                                                    <span>{d.assignedDistributorName ?? distributorsById.get(d.assignedDistributor ?? 0) ?? ''}</span>
                                                                    <span>{reed.installationDate ? dateFormat(reed.installationDate, 'dd-mm-yyyy') : ''}</span>
                                                                    <span />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </>
                        ) : (
                            <tr>
                                <td colSpan={4} className='text-center py-3'>
                                    No devices found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                {!devicesLoading && !summaryLoading && (
                    <PaginationComponent recordsCount={totalElements} pagination={pagination} />
                )}
                {(summaryLoading || devicesSummary) && (
                    <div className='w-100 mt-3 mb-3'>
                        <div className='border rounded overflow-hidden bg-white w-100'>
                            <div className='background-dark color-main d-flex justify-content-between align-items-center px-3 py-2'>
                                <span>
                                    <span className='fw-bold'>{t('summary')}:</span> {summaryFilterText}
                                </span>
                                <span className='small'>
                                    {t('totalBranchesCount')}:{' '}
                                    <span className='fw-bold'>{devicesSummary?.totalObjects ?? totalElements}</span> |{' '}
                                    {t('active')}: <span className='fw-bold'>{devicesSummary?.activeObjects ?? 0}</span>{' '}
                                    | {t('inactive')}:{' '}
                                    <span className='fw-bold'>{devicesSummary?.inactiveObjects ?? 0}</span> |{' '}
                                    {t('servicedObjects')}:{' '}
                                    <span className='fw-bold'>{devicesSummary?.servicedObjects ?? 0}</span> |{' '}
                                    {t('upcomingMaintenance')}:{' '}
                                    <span className='fw-bold'>{devicesSummary?.upcomingMaintenanceObjects ?? 0}</span>
                                </span>
                            </div>
                            {summaryLoading ? (
                                <div className='d-flex justify-content-center align-items-center py-4'>
                                    <Spinner animation='border' size='sm' className='me-2' />
                                    <span>{t('summaryLoading')}</span>
                                </div>
                            ) : (
                                <Table responsive size='sm' className='mb-0 text-center align-middle'>
                                    <thead>
                                        <tr>
                                            <th className='text-start ps-3'>{t('model')}</th>
                                            <th>{t('devices')}</th>
                                            <th>{t('installedReplacementDevices')}</th>
                                            <th>{t('returnedDevices')}</th>
                                            <th>{t('activeDevices')}</th>
                                            <th>{t('pausedDevices')}</th>
                                            <th>{t('servicedDevices')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(devicesSummary?.models ?? []).map((row) => (
                                            <tr key={row.model ?? ''}>
                                                <td className='text-start ps-3'>{row.model ?? ''}</td>
                                                <td>{row.totalDevices}</td>
                                                <td>
                                                    {renderInstalledReplacementDevices(
                                                        row.installedDevices,
                                                        getReplacementReturnedDevices(row)
                                                    )}
                                                </td>
                                                <td>{row.returnedDevices ?? 0}</td>
                                                <td>{row.activeDevices}</td>
                                                <td>{row.pausedDevices}</td>
                                                <td>{row.servicedDevices ?? 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className='fw-bold'>
                                            <td className='text-start ps-3'>{t('totalByModels')}</td>
                                            <td>{deviceSummaryModelTotals.totalDevices}</td>
                                            <td>{netInstalledDevices}</td>
                                            <td>{deviceSummaryModelTotals.returnedDevices}</td>
                                            <td>{deviceSummaryModelTotals.activeDevices}</td>
                                            <td>{deviceSummaryModelTotals.pausedDevices}</td>
                                            <td>{deviceSummaryModelTotals.servicedDevices}</td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            )}
                        </div>
                    </div>
                )}
                {(pricesLoading || hasDistributorPriceSummary) && (
                    <div className='w-100 d-flex justify-content-end px-0 mt-3 mb-2'>
                        <Button
                            type='button'
                            variant='success'
                            disabled={pricesExportLoading}
                            onClick={exportDevicesPricesTable}
                        >
                            {pricesExportLoading && <Spinner animation='border' size='sm' className='me-2' />}
                            {t('exportExcel')}
                        </Button>
                    </div>
                )}
                {(pricesLoading || hasDistributorPriceSummary) && (
                    <div className='w-100 mt-3 mb-3'>
                        <div className='border rounded overflow-hidden bg-white w-100'>
                            <div className='background-dark color-main d-flex justify-content-between align-items-start flex-wrap gap-2 px-3 py-2'>
                                <span>
                                    <span className='fw-bold'>{t('pricesByDistributor')}:</span> {pricesFilterText}
                                </span>
                                {renderPriceColorLegend()}
                            </div>
                            {pricesLoading ? (
                                <div className='d-flex justify-content-center align-items-center py-4'>
                                    <Spinner animation='border' size='sm' className='me-2' />
                                    <span>{t('summaryLoading')}</span>
                                </div>
                            ) : (
                                <Table responsive size='sm' className='mb-0 text-center align-middle'>
                                    <thead>
                                        <tr>
                                            <th className='text-start ps-3'>{t('distributor')}</th>
                                            {deviceModelColumns.map((model, index) => (
                                                <th key={model} style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                    <span
                                                        className='d-inline-block rounded px-2 py-1'
                                                        style={getSoftBadgeStyle(
                                                            DEVICE_MODEL_COLORS[index % DEVICE_MODEL_COLORS.length]
                                                        )}
                                                    >
                                                        {model}
                                                    </span>
                                                </th>
                                            ))}
                                            <th style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                <span className='d-inline-block rounded px-2 py-1' style={getSoftBadgeStyle('#345f8f')}>
                                                    {t('installationBonus')}
                                                </span>
                                            </th>
                                            <th style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                <span className='d-inline-block rounded px-2 py-1' style={getSoftBadgeStyle('#1f583e')}>
                                                    {t('earnedBonus')}
                                                </span>
                                            </th>
                                            <th style={{ borderLeft: '1px solid #e3e7ee' }}><span className='d-inline-block rounded px-2 py-1' style={getSoftBadgeStyle('#8b5e34')}>{t('reedInstallationBonus')}</span></th>
                                            <th style={{ borderLeft: '1px solid #e3e7ee' }}><span className='d-inline-block rounded px-2 py-1' style={getSoftBadgeStyle('#557a46')}>{t('consumableServiceBonus')}</span></th>
                                            <th style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                <span className='d-inline-block rounded px-2 py-1' style={getSoftBadgeStyle('#754f9f')}>
                                                    {t('salesBonus')}
                                                </span>
                                            </th>
                                            <th style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                <span className='d-inline-block rounded px-2 py-1' style={getSoftBadgeStyle('#2f4b57')}>
                                                    {t('totalBonuses')}
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredPricesByDistributor.map((distributor) => {
                                            return (
                                                <tr key={distributor.distributorId}>
                                                    <td className='text-start ps-3'>{distributor.distributorName ?? ''}</td>
                                                    {deviceModelColumns.map((model) => {
                                                        const groups = getDistributorModelPriceGroups(distributor, model);

                                                        return (
                                                            <td key={model} className='px-1' style={{ borderLeft: '1px solid #eef1f5' }}>
                                                                {renderModelPriceGroups(groups)}
                                                            </td>
                                                        );
                                                    })}
                                                    <td
                                                        className='px-2 fw-bold'
                                                        style={{ borderLeft: '1px solid #eef1f5' }}
                                                        title={getInstalledDevicesTooltip(distributor)}
                                                    >
                                                        <div>{formatBonus(getInstallationBonus(distributor))}</div>
                                                        <div className='small fw-normal text-muted'>
                                                            {distributor.installedDevices ?? 0} {t('pcs')}
                                                        </div>
                                                    </td>
                                                    <td className='px-2 fw-bold' style={{ borderLeft: '1px solid #eef1f5' }}>
                                                        {formatBonus(getDistributorEarnedBonus(distributor))}
                                                    </td>
                                                    <td className='px-2 fw-bold' style={{ borderLeft: '1px solid #eef1f5' }} title={getInstalledReedsTooltip(distributor)}><div>{formatBonus(getReedInstallationBonus(distributor))}</div><div className='small fw-normal text-muted'>{distributor.installedReeds ?? 0} {t('pcs')}</div></td>
                                                    <td className='px-2 fw-bold' style={{ borderLeft: '1px solid #eef1f5' }} title={getServicedReedsTooltip(distributor)}><div>{formatBonus(getConsumableServiceBonus(distributor))}</div><div className='small fw-normal text-muted'>{distributor.servicedReeds ?? 0} {t('pcs')}</div></td>
                                                    <td className='px-2 fw-bold' style={{ borderLeft: '1px solid #eef1f5' }}>
                                                        <div>{formatBonus(getDistributorSalesBonus(distributor))}</div>
                                                        <div className='small fw-normal text-muted'>
                                                            {formatBonus(getDistributorSalesTotal(distributor))} /{' '}
                                                            {getDistributorSalesCount(distributor)} {t('salesCountShort')}
                                                        </div>
                                                    </td>
                                                    <td className='px-2 fw-bold' style={{ borderLeft: '1px solid #eef1f5' }}>
                                                        {formatBonus(getDistributorTotalBonus(distributor))}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                    <tfoot>
                                        <tr className='fw-bold'>
                                            <td className='text-start ps-3'>{t('priceSummaryTotal')}</td>
                                            {deviceModelColumns.map((model) => {
                                                const groups = getModelPriceGroupsTotal(model);

                                                return (
                                                    <td key={model} className='px-1' style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                        {renderModelPriceGroups(groups)}
                                                    </td>
                                                );
                                            })}
                                            <td className='px-2' style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                <div>{formatBonus(installationBonusTotal)}</div>
                                                <div className='small fw-normal text-muted'>
                                                    {filteredPricesByDistributor.reduce(
                                                        (total, distributor) => total + (distributor.installedDevices ?? 0),
                                                        0
                                                    )}{' '}
                                                    {t('pcs')}
                                                </div>
                                            </td>
                                            <td className='px-2' style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                {formatBonus(earnedBonusTotal)}
                                            </td>
                                            <td className='px-2' style={{ borderLeft: '1px solid #e3e7ee' }}>{formatBonus(reedInstallationBonusTotal)}</td>
                                            <td className='px-2' style={{ borderLeft: '1px solid #e3e7ee' }}>{formatBonus(consumableServiceBonusTotal)}</td>
                                            <td className='px-2' style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                <div>{formatBonus(salesBonusTotal)}</div>
                                                <div className='small fw-normal text-muted'>
                                                    {formatBonus(salesAmountTotal)} / {salesCountTotal} {t('salesCountShort')}
                                                </div>
                                            </td>
                                            <td className='px-2' style={{ borderLeft: '1px solid #e3e7ee' }}>
                                                {formatBonus(totalBonusTotal)}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
